/* ============================================
   Cart Drawer — Based on Dawn original + demo features
   ============================================ */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._boundClose = this.close.bind(this);
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this._boundClose);
    this.setHeaderCartIconAccessibility();
    this.initGiftNote();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  rebindListeners() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.removeEventListener('click', this._boundClose);
      overlay.addEventListener('click', this._boundClose);
    }
    this.initGiftNote();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');
    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }
    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });
    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.rebindListeners();
      this.open();
      if (window.loadCartRecommendation) window.loadCartRecommendation();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* Refresh cart sections — used by external add-to-cart calls */
  async refreshCartSections() {
    try {
      // Save gift note immediately before refresh
      await this.saveGiftNote();

      const sectionsToRender = this.getSectionsToRender().map(s => s.id).join(',');
      const response = await fetch(`${routes.cart_url}?sections=${sectionsToRender}`);
      if (!response.ok) throw new Error('Failed to fetch cart sections');
      const sections = await response.json();

      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!sectionElement || !sections[section.id]) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
      });

      // Re-bind overlay listener and gift note after HTML replacement
      this.rebindListeners();

      // Refresh product recommendation
      if (window.loadCartRecommendation) window.loadCartRecommendation();
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  /* Save gift note to cart */
  async saveGiftNote() {
    var textarea = this.querySelector('#CartDrawer-GiftNote');
    if (!textarea) return;
    var note = textarea.value;
    try {
      var response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note })
      });
      if (!response.ok) console.error('Gift note save failed:', response.status);
    } catch (e) { /* network error — silent */ }
  }

  /* Bind gift note auto-save */
  initGiftNote() {
    var textarea = this.querySelector('#CartDrawer-GiftNote');
    if (!textarea || textarea._giftNoteInit) return;
    textarea._giftNoteInit = true;
    var self = this;
    var timer;
    textarea.addEventListener('input', function() {
      clearTimeout(timer);
      timer = setTimeout(function() { self.saveGiftNote(); }, 500);
    });
    textarea.addEventListener('blur', function() {
      clearTimeout(timer);
      self.saveGiftNote();
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
